/**
 * The name of the Lightning.out Application
 *
 * @remarks
 * it is the namespace and application name of an Aura Bundle included in our
 * managed package set up for Lightning.out
 */
export const LIGHTNING_OUT_APPLICATION = 'DaScoopComposer:GrooveLightningOut';

export const RENDER_LIGHTNING_COMPONENTS = 'RENDER_LIGHTNING_COMPONENTS';
export const LIGHTNING_USE_FAILURE = 'LIGHTNING_USE_FAILURE';
export const LIGHTNING_RENDER_COMPONENT_FAILURE =
  'LIGHTNING_RENDER_COMPONENT_FAILURE';
export const LIGHTNING_CONTAINER_SIZE_CHANGE =
  'LIGHTNING_CONTAINER_SIZE_CHANGE';
export const LIGHTNING_RENDER_COMPLETED = 'LIGHTNING_RENDER_COMPLETED';
export const LIGHTNING_ASK_IFRAME_READY = 'ASK_IF_READY';
export const LIGHTNING_IFRAME_READY = 'LIGHTNING_IFRAME_READY';

export interface RenderMessage {
  type: typeof RENDER_LIGHTNING_COMPONENTS;
  payload: {
    applicationName: string;
    iframeId: string;
    lightningComponents: string[];
    lightningUrl: string;
    accessToken: string;
    recordId: string;
  };
}

export interface ReadyRequestMessage {
  type: typeof LIGHTNING_ASK_IFRAME_READY;
}

export interface LightningUseErrorMessage {
  type: typeof LIGHTNING_USE_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface LightningRenderComponentErrorMessage {
  type: typeof LIGHTNING_RENDER_COMPONENT_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface LightningContainerSizeChangeMessage {
  type: typeof LIGHTNING_CONTAINER_SIZE_CHANGE;
  payload: {
    iframeId: string;
    clientWidth: number;
    clientHeight: number;
    scrollWidth: number;
    scrollHeight: number;
  };
}

export interface LightningRenderCompletedMessage {
  type: typeof LIGHTNING_RENDER_COMPLETED;
}

export interface LightningIframeReadyMessage {
  type: typeof LIGHTNING_IFRAME_READY;
}

export type LightningRequestMessageTypes = RenderMessage | ReadyRequestMessage;

export type LightningMessageTypes =
  | LightningUseErrorMessage
  | LightningRenderComponentErrorMessage
  | LightningContainerSizeChangeMessage
  | LightningRenderCompletedMessage
  | LightningIframeReadyMessage;

export type CreateComponentStatus = 'SUCCESS' | 'INCOMPLETE' | 'ERROR';

export interface LightningComponentDetails {
  /**
   * A list of components to render at the top of omnibar for this object type
   */
  banner: string[];

  /**
   * An list of additional tabs to render in the main omnibar. Allowing for
   * lightning components to be grouped together into tabbed sections
   */
  tabs: Array<{
    name: string;
    components: string[];
  }>;
}

export interface ContextDetails extends LightningComponentDetails {
  id: string;
  label: string;
}

export interface OrganizationConfiguration {
  /**
   * The name of the salesforce record type (account / contact / etc.) detailing
   * the lightning components to render
   */
  [primaryObjectLabel: string]: LightningComponentDetails;
}

export interface LightningOutScriptInjector {
  isInjected: (document: HTMLDocument) => boolean;
  inject: (document: HTMLDocument, lightningUrl: string) => Promise<void>;
}

export interface LightningOutRenderer {
  /**
   * Handle when $Lightning.use() fails to execute
   *
   * @remarks
   * $Lightning.use() typically fail for a couple of reasons:
   * 1. The salesforce access_token has expired
   * 2. The salesforce organization did not CORS whitelist the domain hosting
   *    omnibar
   */
  onInitializationError?: (e: Error) => void;

  /**
   * Handle when Lightning.out fails to render a Lightning component
   *
   * @remarks
   * This seems to happen when there is a typo in the component/namespace
   * This does not cover runtime errors that can occur within the component
   * itself!
   */
  onComponentError?: (msg: string) => void;

  /**
   * Render all lightning components using the $Lightning module
   */
  render: (
    container: HTMLElement,
    lightningComponents: string[],
    recordId: string,
    lightningUrl: string,
    accessToken: string,
  ) => Promise<void>;
}
